/**
 * src/pages/VersionHistory.js
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

export default function VersionHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [publishing, setPublishing] = useState(null);
  const [activating, setActivating] = useState(null);

  async function fetchVersions() {
    setError('');
    setLoading(true);
    try {
      const res = await api.get('/portal/config/versions');
      setVersions(res.data);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to load version history.');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchVersions(); }, []);

  async function handlePublish(e, version) {
    e.stopPropagation();
    if (!window.confirm(`Publish v${version.version_number}? This will make it the active config for all extensions.`)) return;
    setError('');
    setSuccess('');
    setPublishing(version.id);
    try {
      await api.put(`/portal/config/versions/${version.id}/publish`);
      await fetchVersions();
      setSuccess(`v${version.version_number} published.`);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to publish version.');
      }
    } finally {
      setPublishing(null);
    }
  }

  async function handleActivate(e, version) {
    e.stopPropagation();
    if (!window.confirm(`Make v${version.version_number} the active config? Extensions will pick this up on their next event.`)) return;
    setError('');
    setSuccess('');
    setActivating(version.id);
    try {
      await api.put(`/portal/config/versions/${version.id}/activate`);
      await fetchVersions();
      setSuccess(`v${version.version_number} activated — extensions will pick it up on their next event.`);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to activate version.');
      }
    } finally {
      setActivating(null);
    }
  }

  function handleRowClick(version) {
    navigate(`/config/history/${version.id}`);
  }

  if (loading) return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );

  // Load failed with no data — show only the error, not the misleading empty state
  if (error && versions.length === 0) return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Version History</h1>
          <p className="page-subtitle">
            Click any version to view or edit it. Editing creates a new version based on the selected one.
          </p>
        </div>
      </div>
      <div className="alert alert-error">
        {error}
        <button className="alert-retry" onClick={fetchVersions}>Try again</button>
      </div>
    </div>
  );

  // Active = published version with the most recent published_at timestamp
  const activeVersion = versions
    .filter(v => v.published_at)
    .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))[0];

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Version History</h1>
          <p className="page-subtitle">
            Click any version to view or edit it. Editing creates a new version based on the selected one.
          </p>
        </div>
        {user?.role === 'admin' && (
          <button className="btn btn-primary" onClick={() => navigate('/config')}>
            Create New Version
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {versions.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">No versions yet</div>
            <div className="empty-state-text">Use the Config Editor to create your first version.</div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Config</th>
                  <th>Created by</th>
                  <th>Created</th>
                  <th>Published</th>
                  {user?.role === 'admin' && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {versions.map(v => {
                  const isActive = activeVersion?.id === v.id;
                  const isPublished = !!v.published_at;
                  return (
                    <tr
                      key={v.id}
                      onClick={() => handleRowClick(v)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view or edit this version"
                    >
                      <td>
                        <span className="font-mono" style={{ fontWeight: '500' }}>
                          v{v.version_number}
                        </span>
                      </td>
                      <td>
                        {isActive
                          ? <span className="badge badge-success"><span className="status-dot" />Active</span>
                          : isPublished
                          ? <span className="badge badge-neutral"><span className="status-dot" />Published</span>
                          : <span className="badge badge-warning"><span className="status-dot" />Draft</span>}
                        {v.has_debug && (
                          <span className="badge badge-warning" style={{ marginLeft: '6px' }}>
                            <span className="status-dot" />Debug
                          </span>
                        )}
                      </td>
                      <td>{v.process_count} proc / {v.total_step_count} steps</td>
                      <td>{v.created_by_email}</td>
                      <td className="td-mono">{new Date(v.created_at).toLocaleString()}</td>
                      <td className="td-mono">
                        {v.published_at ? new Date(v.published_at).toLocaleString() : '—'}
                      </td>
                      {user?.role === 'admin' && (
                        <td onClick={e => e.stopPropagation()}>
                          <div className="flex gap-8">
                            {/* Publish button — only for drafts */}
                            {!isPublished && (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={e => handlePublish(e, v)}
                                disabled={publishing === v.id}
                              >
                                {publishing === v.id ? 'Publishing…' : 'Publish'}
                              </button>
                            )}
                            {/* Activate button — only for published non-active versions */}
                            {isPublished && !isActive && (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={e => handleActivate(e, v)}
                                disabled={activating === v.id}
                              >
                                {activating === v.id ? 'Activating…' : 'Make Active'}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}